import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import type { ResourceSeries } from './useResourceSeries';
import type { ResourceSermon, ResourceAsset } from './useSeriesDetail';

export type { ResourceSeries, ResourceSermon, ResourceAsset };

export function useAdminSeries() {
  const [series, setSeries] = useState<ResourceSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('resource_series')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (dbError) throw dbError;
      setSeries(data as ResourceSeries[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load series');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createSeries = useCallback(async (fields: Partial<ResourceSeries>): Promise<ResourceSeries> => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error: dbError } = await supabase
      .from('resource_series')
      .insert({ ...fields, created_by: user?.id })
      .select()
      .single();
    if (dbError) throw dbError;
    await load();
    return data as ResourceSeries;
  }, [load]);

  const updateSeries = useCallback(async (id: string, fields: Partial<ResourceSeries>): Promise<void> => {
    const { error: dbError } = await supabase
      .from('resource_series')
      .update({ ...fields, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (dbError) throw dbError;
    await load();
  }, [load]);

  const deleteSeries = useCallback(async (id: string): Promise<void> => {
    const { error: dbError } = await supabase.from('resource_series').delete().eq('id', id);
    if (dbError) throw dbError;
    await load();
  }, [load]);

  const togglePublished = useCallback(async (id: string, published: boolean): Promise<void> => {
    const { error: dbError } = await supabase
      .from('resource_series')
      .update({ published, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (dbError) throw dbError;
    await load();
  }, [load]);

  const uploadCoverArt = useCallback(async (seriesId: string, file: File): Promise<void> => {
    const ext = file.name.split('.').pop();
    const filePath = `series/${seriesId}/cover/${uuidv4()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('admin-assets')
      .upload(filePath, file, { contentType: file.type, upsert: true });
    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage.from('admin-assets').getPublicUrl(filePath);

    await updateSeries(seriesId, { cover_art_path: filePath, cover_art_url: publicUrl });
  }, [updateSeries]);

  const createResourceSermon = useCallback(async (
    seriesId: string,
    fields: Partial<ResourceSermon>
  ): Promise<ResourceSermon> => {
    const { data, error: dbError } = await supabase
      .from('resource_sermons')
      .insert({ ...fields, series_id: seriesId })
      .select()
      .single();
    if (dbError) throw dbError;
    return data as ResourceSermon;
  }, []);

  const updateResourceSermon = useCallback(async (
    id: string,
    fields: Partial<ResourceSermon>
  ): Promise<void> => {
    const { error: dbError } = await supabase
      .from('resource_sermons')
      .update({ ...fields, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (dbError) throw dbError;
  }, []);

  const deleteResourceSermon = useCallback(async (id: string): Promise<void> => {
    const { error: dbError } = await supabase.from('resource_sermons').delete().eq('id', id);
    if (dbError) throw dbError;
  }, []);

  const uploadAsset = useCallback(async (
    seriesId: string,
    sermonId: string | null,
    file: File,
    label: string,
    assetType: ResourceAsset['asset_type']
  ): Promise<void> => {
    const ext = file.name.split('.').pop();
    const filePath = `series/${seriesId}/assets/${uuidv4()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('admin-assets')
      .upload(filePath, file, { contentType: file.type });
    if (uploadError) throw uploadError;

    const { error: dbError } = await supabase.from('resource_assets').insert({
      series_id: seriesId,
      sermon_id: sermonId,
      label,
      asset_type: assetType,
      file_path: filePath,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
    });
    if (dbError) throw dbError;
  }, []);

  const deleteAsset = useCallback(async (assetId: string, filePath: string): Promise<void> => {
    await supabase.storage.from('admin-assets').remove([filePath]);
    const { error: dbError } = await supabase.from('resource_assets').delete().eq('id', assetId);
    if (dbError) throw dbError;
  }, []);

  return {
    series,
    loading,
    error,
    refresh: load,
    createSeries,
    updateSeries,
    deleteSeries,
    togglePublished,
    uploadCoverArt,
    createResourceSermon,
    updateResourceSermon,
    deleteResourceSermon,
    uploadAsset,
    deleteAsset,
  };
}
